"use client";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";

type DensityResult = {
	phrase: string;
	count: number;
	density: number;
};

type AnalysisResults = {
	totalWords: number;
	uniqueWords: number;
	oneWord: DensityResult[];
	twoWord: DensityResult[];
	threeWord: DensityResult[];
};

const STOP_WORDS = new Set([
	"a",
	"an",
	"the",
	"and",
	"or",
	"but",
	"is",
	"are",
	"was",
	"were",
	"be",
	"been",
	"being",
	"have",
	"has",
	"had",
	"do",
	"does",
	"did",
	"will",
	"would",
	"could",
	"should",
	"may",
	"might",
	"shall",
	"can",
	"need",
	"to",
	"of",
	"in",
	"for",
	"on",
	"with",
	"at",
	"by",
	"from",
	"as",
	"into",
	"through",
	"during",
	"before",
	"after",
	"above",
	"below",
	"between",
	"out",
	"off",
	"over",
	"under",
	"again",
	"further",
	"then",
	"once",
	"here",
	"there",
	"when",
	"where",
	"why",
	"how",
	"all",
	"both",
	"each",
	"few",
	"more",
	"most",
	"other",
	"some",
	"such",
	"no",
	"nor",
	"not",
	"only",
	"own",
	"same",
	"so",
	"than",
	"too",
	"very",
	"just",
	"about",
	"up",
	"its",
	"it",
	"this",
	"that",
	"these",
	"those",
	"i",
	"me",
	"my",
	"we",
	"our",
	"you",
	"your",
	"he",
	"him",
	"his",
	"she",
	"her",
	"they",
	"them",
	"their",
	"what",
	"which",
	"who",
	"whom",
	"if",
	"also",
	"am",
	"don",
	"t",
	"s",
	"re",
	"ve",
	"ll",
	"d",
	"m",
]);

function tokenize(text: string): string[] {
	return text
		.toLowerCase()
		.replace(/[^a-z0-9\s'-]/g, " ")
		.split(/\s+/)
		.filter((w) => w.length > 1);
}

function getNgrams(words: string[], n: number, totalWords: number): DensityResult[] {
	const counts = new Map<string, number>();
	const filteredWords = words.filter((w) => !STOP_WORDS.has(w));

	if (n === 1) {
		for (const word of filteredWords) {
			counts.set(word, (counts.get(word) ?? 0) + 1);
		}
	} else {
		for (let i = 0; i <= words.length - n; i++) {
			const gram = words.slice(i, i + n);
			if (gram.some((w) => STOP_WORDS.has(w))) continue;
			const phrase = gram.join(" ");
			counts.set(phrase, (counts.get(phrase) ?? 0) + 1);
		}
	}

	return Array.from(counts.entries())
		.filter(([, count]) => count >= 2)
		.map(([phrase, count]) => ({
			phrase,
			count,
			density: (count / totalWords) * 100,
		}))
		.sort((a, b) => b.count - a.count)
		.slice(0, 20);
}

function analyze(text: string): AnalysisResults {
	const words = tokenize(text);
	const totalWords = words.length;
	const uniqueWords = new Set(words).size;

	return {
		totalWords,
		uniqueWords,
		oneWord: getNgrams(words, 1, totalWords),
		twoWord: getNgrams(words, 2, totalWords),
		threeWord: getNgrams(words, 3, totalWords),
	};
}

function DensityTable({ results, label }: { results: DensityResult[]; label: string }) {
	if (results.length === 0) {
		return (
			<p className="py-4 text-center font-mono text-xs text-muted-foreground/50">
				No {label} phrases appear more than once.
			</p>
		);
	}

	return (
		<div className="overflow-hidden rounded-lg border border-border">
			<table className="w-full">
				<thead>
					<tr className="border-b border-border bg-muted/30">
						<th className="px-4 py-2.5 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
							{label}
						</th>
						<th className="px-4 py-2.5 text-right font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
							Count
						</th>
						<th className="px-4 py-2.5 text-right font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
							Density
						</th>
					</tr>
				</thead>
				<tbody>
					{results.map((r) => (
						<tr key={r.phrase} className="border-b border-border/50 last:border-0">
							<td className="px-4 py-2 font-mono text-sm text-foreground">{r.phrase}</td>
							<td className="px-4 py-2 text-right font-mono text-sm text-muted-foreground">
								{r.count}
							</td>
							<td className="px-4 py-2 text-right font-mono text-sm text-muted-foreground">
								{r.density.toFixed(2)}%
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function KeywordDensityForm() {
	const [text, setText] = useState("");
	const [results, setResults] = useState<AnalysisResults | null>(null);
	const [activeTab, setActiveTab] = useState<"one" | "two" | "three">("one");

	function handleAnalyze(e: React.FormEvent) {
		e.preventDefault();
		if (!text.trim()) return;
		setResults(analyze(text));
	}

	function handleClear() {
		setText("");
		setResults(null);
	}

	const tabs = [
		{ key: "one" as const, label: "1-word" },
		{ key: "two" as const, label: "2-word" },
		{ key: "three" as const, label: "3-word" },
	];

	return (
		<div className="w-full">
			<form onSubmit={handleAnalyze} className="flex w-full flex-col gap-3">
				<Textarea
					value={text}
					onChange={(e) => setText(e.target.value)}
					placeholder="Paste your content here..."
					className="min-h-[200px] resize-y border-border/50 bg-background/80 font-mono text-sm backdrop-blur-sm placeholder:text-muted-foreground/40 focus:border-indigo-500/50"
				/>
				<div className="flex gap-2">
					<Button
						type="submit"
						disabled={!text.trim()}
						className="flex-1 bg-indigo-500 font-mono text-sm tracking-wide text-white shadow-lg shadow-indigo-500/20 transition-all hover:bg-indigo-400 hover:shadow-indigo-500/30 disabled:opacity-40"
					>
						Analyze density
					</Button>
					{results && (
						<Button
							type="button"
							variant="outline"
							onClick={handleClear}
							className="font-mono text-sm tracking-wide"
						>
							Clear
						</Button>
					)}
				</div>
			</form>

			{results && (
				<div className="mt-8 space-y-6">
					<div className="flex gap-6">
						<div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
							<p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
								Total words
							</p>
							<p className="mt-1 font-mono text-2xl font-bold text-foreground">
								{results.totalWords.toLocaleString()}
							</p>
						</div>
						<div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
							<p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
								Unique words
							</p>
							<p className="mt-1 font-mono text-2xl font-bold text-foreground">
								{results.uniqueWords.toLocaleString()}
							</p>
						</div>
					</div>

					<div>
						<div className="mb-3 flex gap-1">
							{tabs.map((tab) => (
								<button
									key={tab.key}
									type="button"
									onClick={() => setActiveTab(tab.key)}
									className={`rounded-md px-3 py-1.5 font-mono text-xs transition-colors ${
										activeTab === tab.key
											? "bg-indigo-500/10 text-indigo-500"
											: "text-muted-foreground hover:text-foreground"
									}`}
								>
									{tab.label}
								</button>
							))}
						</div>

						{activeTab === "one" && <DensityTable results={results.oneWord} label="Keyword" />}
						{activeTab === "two" && <DensityTable results={results.twoWord} label="Phrase" />}
						{activeTab === "three" && <DensityTable results={results.threeWord} label="Phrase" />}
					</div>
				</div>
			)}
		</div>
	);
}
